export type RcsComponentType =
  | 'text'
  | 'image'
  | 'video'
  | 'rich_card'
  | 'carousel'
  | 'suggested_replies'
  | 'cta_buttons'
  | 'call_to_action'
  | 'location'
  | 'divider';

export type RcsCardOrientation = 'VERTICAL' | 'HORIZONTAL';
export type RcsMediaHeight = 'SHORT' | 'MEDIUM' | 'TALL';
export type RcsCardWidth = 'SMALL' | 'MEDIUM';

export interface RcsButton {
  id: string;
  text: string;
  action: 'open_url' | 'dial' | 'location' | 'calendar';
  value: string;
}

export type RcsActionType = 'URL' | 'PHONE_NUMBER' | 'LOCATION' | 'CALENDAR';

export interface RcsApiAction {
  type: RcsActionType;
  text: string;
  url?: string;
  phone_number?: string;
}

export interface RcsSuggestedReply {
  id: string;
  text: string;
  postbackData: string;
}

export interface RcsCard {
  id: string;
  title: string;
  description: string;
  mediaUrl?: string;
  mediaFile?: File | null;
  mediaHeight?: RcsMediaHeight;
  buttons?: RcsButton[];
}

export interface RcsComponent {
  id: string;
  type: RcsComponentType;
  content?: string;
  title?: string;
  description?: string;
  mediaUrl?: string;
  mediaFile?: File | null;
  mediaCaption?: string;
  suggestedReplies?: RcsSuggestedReply[];
  replies?: string[];
  buttons?: RcsButton[];
  cards?: RcsCard[];
  latitude?: number;
  longitude?: number;
  locationLabel?: string;
  locationText?: string;
  ctaText?: string;
  orientation?: RcsCardOrientation;
  mediaHeight?: RcsMediaHeight;
  cardWidth?: RcsCardWidth;
  carouselText?: string;
}

export const buttonToApiAction = (button: RcsButton): RcsApiAction => {
  const actionTypeMap: Record<RcsButton['action'], RcsActionType> = {
    open_url: 'URL',
    dial: 'PHONE_NUMBER',
    location: 'LOCATION',
    calendar: 'CALENDAR',
  };

  const action: RcsApiAction = {
    type: actionTypeMap[button.action],
    text: button.text,
  };

  if (button.action === 'open_url') {
    action.url = button.value;
  } else if (button.action === 'dial') {
    action.phone_number = button.value;
  }

  return action;
};

export type RcsUseCase =
  | 'Order Update'
  | 'Shipping'
  | 'Appointment'
  | 'Payment'
  | 'Account'
  | 'Promotional'
  | 'Other';

export const RCS_USE_CASES: { label: string; value: RcsUseCase }[] = [
  { label: 'Order Update', value: 'Order Update' },
  { label: 'Shipping', value: 'Shipping' },
  { label: 'Appointment', value: 'Appointment' },
  { label: 'Payment', value: 'Payment' },
  { label: 'Account', value: 'Account' },
  { label: 'Promotional', value: 'Promotional' },
  { label: 'Other', value: 'Other' },
];

export const COMPONENT_PALETTE: { type: RcsComponentType; label: string; description: string }[] = [
  { type: 'text', label: 'Text', description: 'Add simple text message' },
  { type: 'image', label: 'Image', description: 'Add image (JPG, PNG)' },
  { type: 'video', label: 'Video', description: 'Add video (MP4)' },
  { type: 'rich_card', label: 'Rich Card', description: 'Add rich card with title, description & media' },
  { type: 'carousel', label: 'Carousel', description: 'Add scrollable cards' },
  { type: 'suggested_replies', label: 'Suggested Replies', description: 'Add quick reply buttons' },
  { type: 'cta_buttons', label: 'Call to Action', description: 'Add buttons with actions' },
  { type: 'location', label: 'Location', description: 'Add location / map' },
  { type: 'divider', label: 'Divider', description: 'Add divider between components' },
];

export const createDefaultComponent = (type: RcsComponentType): RcsComponent => {
  const id = `${type}_${Date.now()}`;

  switch (type) {
    case 'text':
      return { id, type, content: '' };
    case 'image':
    case 'video':
      return { id, type, mediaUrl: '', mediaFile: null, mediaCaption: '' };
    case 'rich_card':
      return {
        id,
        type,
        title: '',
        description: '',
        mediaUrl: '',
        mediaFile: null,
        buttons: [],
        orientation: 'VERTICAL',
        mediaHeight: 'MEDIUM',
      };
    case 'carousel':
      return {
        id,
        type,
        cards: [],
        cardWidth: 'MEDIUM',
        carouselText: '',
      };
    case 'suggested_replies':
      return {
        id,
        type,
        content: '',
        suggestedReplies: [{ id: `reply_${Date.now()}`, text: '', postbackData: '' }],
      };
    case 'cta_buttons':
      return {
        id,
        type,
        ctaText: '',
        buttons: [{ id: `btn_${Date.now()}`, text: '', action: 'dial', value: '' }],
      };
    case 'location':
      return {
        id,
        type,
        latitude: undefined,
        longitude: undefined,
        locationLabel: '',
        locationText: '',
      };
    case 'divider':
      return { id, type };
    default:
      return { id, type };
  }
};
